<template>
  <div class="actuals-page-root" style="margin:20px">
    <FiPageRail title="Bookings & Actuals">
      <template #actions>
          <b-button size="sm" variant="danger" title="View actuals errors grouped by cause with suggested fixes"
                    :to="{ path: '/actuals_troubleshooter', query: { source: 'fasttrak', fromDate: details_fromDate, toDate: details_toDate, filter: actuals_filter, company: selected_company, type: selected_type } }">
            <i class="fa fa-exclamation-triangle" style="margin-right: 4px;"></i> <b>Troubleshoot Errors</b>
          </b-button>
          <actuals-troubleshooter-help source="fasttrak"/>
      </template>
    </FiPageRail>

    <!-- date presets, then the loaded range as two chips that swap themselves for
         their own picker on click, flanked by day-nudge arrows; presets and arrows
         reload on their own, but a hand-picked From/To waits for Search so that
         setting two dates fires one query instead of one per keystroke -->
    <div class="actuals-fasttrak-toolbar">
      <div class="actuals-date-presets" role="group" aria-label="Date range">
        <span class="actuals-range-label">Range</span>
        <button
          v-for="opt in quick_range_options"
          :key="'qr-' + opt.value"
          type="button"
          :class="['actuals-date-chip', {'actuals-date-chip--active': !is_custom_range && quick_range_active_days === opt.value}]"
          :aria-pressed="!is_custom_range && quick_range_active_days === opt.value ? 'true' : 'false'"
          :title="opt.title"
          v-on:click.prevent="set_quick_range(opt.value)"
        >{{ opt.label }}</button>
        <button
          type="button"
          class="actuals-date-chip actuals-date-chip--custom"
          :class="{'actuals-date-chip--active': dates_open}"
          :aria-pressed="dates_open ? 'true' : 'false'"
          :aria-expanded="dates_open ? 'true' : 'false'"
          aria-controls="actuals-fasttrak-dates"
          aria-label="Set a specific From / To window"
          title="Set a specific From / To window"
          @click="show_custom_dates = !show_custom_dates"
        >Custom</button>
      </div>
      <div
        id="actuals-fasttrak-dates"
        class="actuals-filter-dates"
        :class="{'actuals-filter-dates--open': dates_open}"
      >
        <span class="actuals-filter-dateunit">
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift back one window" v-on:click.prevent="nudge_range_days(-selected_day_count)"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Previous day" v-on:click.prevent="nudge_range_days(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
          </b-button-group>
          <span class="actuals-filter-field">
            <span class="actuals-range-label">From</span>
            <b-form-input v-model="from_date_iso" type="date" size="sm" class="actuals-filter-date" :min="date_min_iso" :max="date_max_iso"/>
          </span>
        </span>
        <span class="actuals-filter-dateunit">
          <span class="actuals-filter-field">
            <span class="actuals-range-label">To</span>
            <b-form-input v-model="to_date_iso" type="date" size="sm" class="actuals-filter-date" :min="date_min_iso" :max="date_max_iso"/>
          </span>
          <b-button-group class="toolbar-date-shifts">
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Next day" v-on:click.prevent="nudge_range_days(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
            <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift forward one window" v-on:click.prevent="nudge_range_days(selected_day_count)"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
          </b-button-group>
        </span>
        <b-form-select size="sm" v-model="actuals_filter" :options="actuals_filter_options" class="toolbar-select actuals-range-mode"/>
      </div>
      <span class="toolbar-group">
        <span class="actuals-range-label">By ID</span>
        <b-form-input size="sm" class="toolbar-input" v-model="booking_search" placeholder="reservation id"/>
      </span>
      <b-form-select size="sm" v-if="company_options" v-model="selected_company" :options="company_options" class="toolbar-select"/>
      <b-form-select size="sm" v-if="type_options" v-model="selected_type" :options="type_options" class="toolbar-select toolbar-type"/>
      <b-form-select size="sm" v-if="show_base_select" v-model="selected_base" :options="base_options" class="toolbar-select toolbar-base"/>
      <span class="toolbar-group">
        <span class="actuals-range-label">Filter</span>
        <b-form-input size="sm" class="toolbar-input" v-model="booking_filter" placeholder="filter"/>
      </span>
      <b-button
        size="sm"
        class="toolbar-btn"
        :variant="range_pending ? 'primary' : 'secondary'"
        :title="range_pending ? 'Search the picked date range -- the results below are still the previous range' : 'Search'"
        v-on:click.prevent="run_search()"
      >Search</b-button>
    </div>

    <div v-if="!this.$route.query.booking_id && !loading" class="actuals-fasttrak-filter-area">
      <div class="actuals-fasttrak-status-filter-row">
        <div class="actuals-filter-chips actuals-fasttrak-filter-chips">
          <span class="actuals-filter-label">Status</span>
          <span @click="filter_by_status(null)" class="actuals-chip actuals-chip-clickable actuals-chip-total"
                :class="{'actuals-chip-selected': !selected_status}"
                title="Show every row in the loaded range">{{ items.length }} of {{ items_full.length }}</span>
          <span @click="filter_by_status('success')" class="actuals-chip actuals-chip-clickable success"
                :class="{'actuals-chip-selected': selected_status == 'success'}">Success {{ success_cnt }}</span>
          <span @click="filter_by_status('error')" class="actuals-chip actuals-chip-clickable error"
                :class="{'actuals-chip-selected': selected_status == 'error'}">Errors {{ error_cnt }}</span>
          <span @click="filter_by_status('cancelled')" class="actuals-chip actuals-chip-clickable cancelled"
                :class="{'actuals-chip-selected': selected_status == 'cancelled'}">Cancelled {{ cancelled_cnt }}</span>
          <span @click="filter_by_status('skipped')" class="actuals-chip actuals-chip-clickable skipped"
                :class="{'actuals-chip-selected': selected_status == 'skipped'}">Skipped {{ skipped_cnt }}</span>
          <span @click="filter_by_status('unprocessed')" class="actuals-chip actuals-chip-clickable unprocessed"
                :class="{'actuals-chip-selected': selected_status == 'unprocessed'}">Unprocessed {{ unprocessed_cnt }}</span>
        </div>
      </div>
    </div>

    <div>
      <b-table :key="actuals_table_key" :busy="loading" :sticky-header="actuals_sticky_header" bordered
               :stacked="cardView"
               table-class="actuals-fasttrak-table" :items="items" :fields="table_fields"
               :current-page="currentPage" :tbody-tr-class="get_row_color"
               :per-page="perPage"
               show-empty :empty-text="empty_message">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="status_msg" slot-scope="row">
          <!-- v-for outranks v-if in vue 2, so the guard has to sit on a
               wrapper: a row with no status_msg (every moovs row, until an
               actuals processor exists) would otherwise call .split on
               undefined and abort the whole tbody render. -->
          <div v-if="row.item.status_msg">
            <div style="text-align: left" v-for="(msg, idx) in row.item.status_msg.split(',')" :key="'status-msg-' + idx">
              <error-category-popover v-if="row.item.status === 'error'" :message="msg" source="fasttrak"/><span v-html="msg"/>
            </div>
          </div>
        </template>
<!--        -->
<!--        <template slot="status_msg" slot-scope="row">-->
<!--          <div v-if="row.item.actual && row.item.actual.err_msgs && row.item.actual.err_msgs.length" style="text-align: left">-->
<!--            <div v-for="(msg, idx) in row.item.actual.err_msgs" :key="idx">-->
<!--              <span v-html="msg"/>-->
<!--            </div>-->
<!--          </div>-->
<!--        </template>-->

        <template slot="notes" slot-scope="row">
          <b-form-input style="width:250px" v-model="row.item.notes" v-on:blur="save_notes(row.item)" @keydown.enter.native="save_notes(row.item)"/>
        </template>

        <template slot="reprocess" slot-scope="row">
          <span v-if="enable_reprocess" size="sm" @click="reprocess_alert(row.item)"><span style="font-size: 35px;">&#8635;</span></span>
        </template>

        <template slot="scheduled_departure_time" slot-scope="row">
          {{row.item.scheduled_departure_time | formatDateTimeSmall}}
        </template>

        <template slot="scheduled_pickup_time" slot-scope="row">
          {{row.item.scheduled_pickup_time | formatDateTimeSmall}}
        </template>

        <template slot="scheduled_end_time" slot-scope="row">
          {{row.item.scheduled_end_time | formatDateTimeSmall}}
        </template>

        <template slot="modified_time" slot-scope="row">
          {{row.item.modified_time | formatDateTime}}
        </template>

        <template slot="details" slot-scope="row">
          <div style="align-content: center">
            <b-button size="sm" @click="row.toggleDetails">
              {{ row.detailsShowing ? '-' : '+' }}
            </b-button>&nbsp;
          </div>
        </template>

        <template slot="row-details" slot-scope="row">
          <b-card style="text-align: left; border: 1px solid grey">
            <table width="100%">
              <tr>
                <th>Event</th>
                <th>Planned Time</th>
                <th>Engine Time</th>
                <th>Engine Delta</th>
                <th>Vehicle Actual Time</th>
                <th>Vehicle Delta</th>
                <th>Planned Location</th>
              </tr>
              <tr>
                <td>Start Time</td>
                <td>{{row.item.actual.movement.StartDateTime | formatDateTimeTZ}}</td>
                <td>{{row.item.actual.nearestEngineOnStartDateTime | formatDateTimeTZ}}</td>
                <td>{{get_time_diff(row.item.actual.plannedStartDateTime,row.item.actual.nearestEngineOnStartDateTime)}}</td>
                <td>{{row.item.actual.nearestLocationStartDateTime | formatDateTimeTZ}}</td>
                <td>{{get_time_diff(row.item.actual.plannedStartDateTime,row.item.actual.nearestLocationStartDateTime)}}</td>
                <td><span v-html="row.item.actual.pickupMapLink"/></td>
              </tr>
              <tr>
                <td>Pickup Time</td>
                <td>{{row.item.actual.movement.PickupDateTime | formatDateTimeTZ}}</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>{{row.item.actual.actualPickupDateTime | formatDateTimeTZ}}</td>
                <td>{{get_time_diff(row.item.actual.plannedPickupDateTime,row.item.actual.actualPickupDateTime)}}</td>
                <td><span v-html="row.item.actual.pickupMapLink"/></td>
              </tr>
              <tr>
                <td>Finish Time</td>
                <td>{{row.item.actual.movement.FinishDateTime | formatDateTimeTZ}}</td>
                <td>{{row.item.actual.nearestEngineOffFinishDateTime | formatDateTimeTZ}}</td>
                <td>{{get_time_diff(row.item.actual.plannedFinishDateTime,row.item.actual.nearestEngineOffFinishDateTime)}}</td>
                <td>{{row.item.actual.nearestLocationFinishDateTime | formatDateTimeTZ}}</td>
                <td>{{get_time_diff(row.item.actual.plannedFinishDateTime,row.item.actual.nearestLocationFinishDateTime)}}</td>
                <td><span v-html="row.item.actual.finishMapLink"/></td>
              </tr>
            </table>
            <br/>
            <div v-html="row.item.actual.samsara_links"/>
            <span>
              <b>Map Links: </b>
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/vehicle-track/' + user.tenant + '/' + row.item.vehicle }">Track Vehicle</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/bookingType/' + row.item.bookingType }">Track '{{row.item.bookingType}}' Vehicles</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/clientID/' + row.item.clientID }">Track '{{row.item.clientID}}' Vehicles</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/booking/' + row.item.charterID }">Track Booking '{{row.item.charterID}}' Vehicles</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/movement/' + row.item.bookingID }">Track Movement '{{row.item.movementID}}' Vehicles</router-link>
              &nbsp;|&nbsp;
              <router-link style="font-weight: bold;" :to="{ path: '/vehicle_trip_history?vehicleId=' + row.item.vehicle }">Trip History</router-link>
            </span>

            <div class="row" style="margin: 10px 0px 0px 10px">
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.nearestEngineOnStartDateTime || row.item.actual.nearestLocationStartDateTime">
                <b>Start Event Times:</b><br/>
                Vehicle Key On: {{row.item.actual.nearestEngineOnStartDateTime | formatDateTime}}<br/>
                Vehicle Start Leave: {{row.item.actual.nearestLocationStartDateTime | formatDateTime}}
              </span>
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.nearestEngineOffFinishDateTime || row.item.actual.nearestLocationFinishDateTime">
                <b>Finish Event Times:</b><br/>
                Vehicle Key Off: {{row.item.actual.nearestEngineOffFinishDateTime | formatDateTime}}<br/>
                Vehicle Finish Arrival: {{row.item.actual.nearestLocationFinishDateTime | formatDateTime}}
              </span>
            </div>
            <div class="row" style="margin: 10px 0px 0px 10px">
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.actualTotalDistance">
                <b>Distances (miles)</b><br/>
                Planned Total: {{row.item.plannedDistance}}<br/>
                Trip Total: {{row.item.actual.actualTotalDistance}}<br/>
                Start Odometer: {{row.item.actual.actualStartOdometer}}<br/>
                End Odometer: {{row.item.actual.actualEndOdometer}}<br/>
                Odometer Total: {{row.item.actual.actualEndOdometer - row.item.actual.actualStartOdometer}}
              </span>
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.jurisdictionDistances">
                <b>State Distances (miles)</b><br/>
                <div v-for="dist in row.item.actual.jurisdictionDistances">
                  {{dist.jurisdiction}} : {{dist.distance}}m
                </div>
              </span>
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.actualLiveDistance">
                <b>Live & Dead Mileage (miles):</b><br/>
                Live Mileage: {{row.item.actual.actualLiveDistance}}<br/>
                Dead Mileage: {{row.item.actual.actualEmptyDistance}}{{row.item.actual.actualDeadDistance}}<br/>
              </span>
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.actualHOSOnDuration">
                <b>Driver HOS Activity (minutes):</b><br/>
                Total On Duty: {{row.item.actual.actualHOSOnDuration}}<br/>
                Total Off Duty: {{row.item.actual.actualHOSOffDuration}}<br/>
                Total Driving : {{row.item.actual.actualHOSDrivingDuration}}<br/>
              </span>
            </div>
          </b-card>
        </template>
      </b-table>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items.length"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>

    import {api_url, get_fasttrak_actuals } from '../networking/apis'
    import {mapState} from "vuex";
    import moment from 'moment'
    import {justDate} from "@/helpers/dates";
    import { excludeSkippedUnlessSelected } from '@/helpers/actuals_status_filter'
    import error_category_popover from '@/components/error_category_popover'
    import actuals_troubleshooter_help from '@/components/actuals_troubleshooter_help'
    import axios from 'axios';
    import vsmMobileView from '@/mixins/vsm_mobile_view'

    export default {
      name: 'actuals_fasttrak',
      components: {
        'error-category-popover': error_category_popover,
        'actuals-troubleshooter-help': actuals_troubleshooter_help,
      },
      // matchMedia-driven mobileView / cardView flags
      mixins: [vsmMobileView],

      data() {
        return {
          msg: 'actuals',
          // preset windows for the date chips; a range that matches none of
          // them reads as Custom rather than as no selection at all
          quick_range_options: [
            { value: 1, label: '1d', title: 'Today' },
            { value: 3, label: '3d', title: 'Last 3 days' },
            { value: 7, label: '7d', title: 'Last 7 days' },
          ],
          show_custom_dates: false,
          // the window the grid below is actually showing, so a hand-picked
          // date can mark the Search button as pending
          loaded_fromDate: '',
          loaded_toDate: '',
          items: [],
          items_full: [],
          selected_status: null,
          summary: [],
          loading: true,
          company_options: null,
          type_options: null,
          base_options: [],
          selected_company: '',
          selected_base: '',
          selected_type: '',
          details_fromDate: justDate(moment()),
          details_toDate: justDate(moment()),
          totalRows: 1,
          currentPage: 1,
          perPage: 100,
          pages: [],
          actuals_filter: 'finish',
          booking_filter: '',
          booking_search: '',
          actuals_filter_options: [
            { value: 'finish', text: 'By Finish Date' },
            { value: 'start', text: 'By Start Date' },
            { value: 'overlap', text: 'Overlaps Range' },
          ],
          fields: [
            { key: 'details', label: '', sortable: false},
            { key: 'reservation_id', sortable: true},
            { key: 'reservation_status', label: 'Resv Status', sortable: true},
            { key: 'modified_time', label: 'Last Updated', sortable: true},
            { key: 'vehicle_name', label:'Vehicle', sortable: true},
            { key: 'driver_name', label: 'Driver', sortable: true},
            { key: 'trip_type', label: 'Type', sortable: true},
            { key: 'scheduled_departure_time', label:'Departure Time', sortable: true},
            { key: 'scheduled_pickup_time', label:'Pickup Time', sortable: true},
            { key: 'from_location', label: 'Pickup', sortable: true},
            { key: 'scheduled_end_time', label: 'Dropoff', sortable: true},
            { key: 'to_location', label: 'Dropoff Time', sortable: true},
            { key: 'status_msg', label: 'Status Message', sortable: false, thStyle: { width: "350px" }},
          ],
          enable_reprocess: false
          ,
          options: {
            format: 'MM-DD-YYYY',
            useCurrent: true,
            minDate: moment().subtract(360, 'days'),
            maxDate: moment().add(180, 'days'),
            keepOpen: false,
            showClear: true,
            showClose: true,
            toolbarPlacement: 'top',
          },

        }
      },

      async created() {
        this.fields_all = this.fields
        this.check_features()
      },
      methods: {
        // the toolbar Search button drives both lookups: a typed booking id when
        // there is one, otherwise the date window sitting in the pickers
        run_search() {
          if (this.booking_search)
            this.search_by_id()
          else
            this.search_by_date_range()
        },
        set_quick_range(days) {
          this.details_fromDate = moment(this.details_toDate, 'MM-DD-YYYY').subtract(days - 1, 'days').format('MM-DD-YYYY')
          this.scheduleRangeLoad()
        },
        nudge_range_days(shift) {
          this.shift_detail_graph_date(shift)
          this.scheduleRangeLoad()
        },
        // coalesced so one toolbar action is one request, and so the reload runs
        // after the date watchers have settled rather than on the pre-clamp dates
        scheduleRangeLoad() {
          if (this._range_load_scheduled)
            return
          this._range_load_scheduled = true
          this.$nextTick(() => {
            this._range_load_scheduled = false
            this.search_by_date_range()
          })
        },
        async check_features() {
          this.check_cols()
          this.load_data()
        },
        async check_cols() {
          this.delete_enabled = this.isEffectiveAdmin || this.user.role == 'customer-admin'
          this.fields = this.fields_all
          if (!this.delete_enabled)
            this.fields = this.fields.filter(item => item.key != 'delete')
          if (!this.isEffectiveAdmin)
            this.fields = this.fields.filter(item => item.key != 'bookingID' && item.key != 'last_updated')
          if (!this.user.features[this.user.tenant].booking_notes_enabled)
            this.fields = this.fields.filter(item => item.key != 'notes')
        },
        async search_by_date_range() {
          this.booking_search = ''
          if (this.$route.query.booking_id) {
            const query = { ...this.$route.query }
            delete query.booking_id
            await this.$router.replace({ query })
          }
          this.load_data()
        },
        async search_by_id() {
          this.booking_filter = ''
          this.load_data()
        },
        async load_data() {
          this.loading = true
          this.loaded_fromDate = this.details_fromDate
          this.loaded_toDate = this.details_toDate
          try {
            if (this.$route.query.booking_id && !this.booking_search)
              this.booking_search = this.$route.query.booking_id

            try {
              const {value: {data = []}} = await get_fasttrak_actuals(this.user.tenant, this.actuals_filter, this.booking_search, this.selected_company, this.selected_type, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
              this.summary = data.summary
              moment.tz.setDefault(this.summary.timezone)
              this.items = data.results
              this.items_full = data.results

              if (data.summary.companies && data.summary.companies.length > 2)
                this.company_options = data.summary.companies

              this.type_options = data.summary.types
              this.base_options = data.summary.bases
              this.filter_results()

              if (this.summary.total_cnt != this.items_full.length) {
                this.$emit('showAlert', 'too many results to process for ' + this.booking_search + ', showing partial results', 'warning', 10000)
              }
            } catch(e){
              this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
            }
          } catch(e){
            this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
          }
          this.loading = false
        },
        async download(){
          this.$emit('showAlert', 'processing download request', 'info', 10000)
          axios.defaults.headers.common.Accept = {}
          axios.post(
              `${api_url}bookings/actuals/download`,
              {
                tenant: this.user.tenant,
                filter: this.actuals_filter,
                company: this.selected_company,
                type: this.selected_type,
                status: this.selected_status,
                fromDate: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
                toDate: moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
              }, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('jwt')}`,
                  Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                },
                responseType: 'blob'
              }
          ).then((response) => {
            axios.defaults.headers.common.Accept = 'application/json'
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute(
                'download',
                `actuals-${this.user.tenant}-${this.details_fromDate}-${this.details_toDate}.csv`
            )
            document.body.appendChild(link)
            link.click()
            this.$emit('showAlert', 'download complete', 'success', 5000)
          }).catch(e => {
            axios.defaults.headers.common.Accept = 'application/json'
            this.$emit('showAlert', 'actuals data could not be downloaded.', 'danger')
          })
        },
        async shift_detail_graph_date(shift) {
          this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
          this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY');
          this.booking_id_filter = ''
        },
        get_time_diff(time1, time2) {
          if (time1 != null && time2 != null)
            return Math.round((new Date(time2) - new Date(time1)) / (1000 * 60)) + 'm';
          return ''
        },
        toggle_div(item,field) {
          item[field] = !item[field];
          this.$forceUpdate();
        },
        get_row_color(item, type) {
            if (!item || type !== 'row') return
            return item.status
        },
        filter_by_status(status) {
          if (status == null || this.selected_status == status) {
            this.selected_status = null
          } else {
            this.selected_status = status
          }
          this.filter_results()
        },
        filter_results() {
          this.items = excludeSkippedUnlessSelected(this.items_full, this.selected_status)
          if (this.booking_filter) {
            const val = this.booking_filter

            this.items = this.items.filter(item => (this.contains(item.bookingID, val) ||
                this.contains(item.driver_name, val) ||
                this.contains(item.vehicle_name, val) ||
                this.contains(item.to_lcoation, val) ||
                this.contains(item.from_location, val) ||
                this.contains(item.trip_type, val) ||
                this.contains(item.passenger_name, val) ||
                this.contains(item.reservation_id, val) ||
                this.contains(item.reservation_status, val)))
          }

          if (this.selected_status) {
            this.items = this.items.filter(item => item.status == this.selected_status)
          }
          if (this.selected_company) {
            this.items = this.items.filter(item => this.contains(item.companyID,this.selected_company))
          }
          if (this.selected_base) {
            this.items = this.items.filter(item => this.contains(item.bookingBase,this.selected_base) || this.contains(item.startBase,this.selected_base) || this.contains(item.finishBase,this.selected_base))
          }
        },
        contains(val1, val2) {
          if (val1 != null) {
            return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
          }
          return false;
        }
      },
      computed:{
        // b-table renders nothing at all without show-empty -- no header, no
        // message -- so an empty date range looks identical to a failed load.
        // name which of the three reasons it actually is.
        empty_message() {
          if (this.booking_search)
            return 'No booking found for "' + this.booking_search + '".'
          if (this.items_full && this.items_full.length)
            return 'No bookings match the current filter. ' + this.items_full.length + ' loaded in this date range.'
          return 'No bookings between ' + this.details_fromDate + ' and ' + this.details_toDate + '. Try a wider date range.'
        },
        show_base_select() {
          return this.base_options && this.base_options.length > 2
        },
        // a hand-picked date leaves the grid on the previous window, so the Search
        // button carries the fact that the two no longer agree
        range_pending() {
          if (!this.loaded_fromDate)
            return false
          return this.details_fromDate !== this.loaded_fromDate
            || this.details_toDate !== this.loaded_toDate
        },
        // native date inputs speak ISO; the page stores MM-DD-YYYY
        from_date_iso: {
          get() { return moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
          set(v) {
            if (!v) return
            this.details_fromDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY')
          },
        },
        to_date_iso: {
          get() { return moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD') },
          set(v) {
            if (!v) return
            this.details_toDate = moment(v, 'YYYY-MM-DD').format('MM-DD-YYYY')
          },
        },
        date_min_iso() {
          return moment(this.options.minDate).format('YYYY-MM-DD')
        },
        date_max_iso() {
          return moment(this.options.maxDate).format('YYYY-MM-DD')
        },
        is_custom_range() {
          return !this.quick_range_options.some(opt => opt.value === this.quick_range_active_days)
        },
        dates_open() {
          return this.show_custom_dates || this.is_custom_range
        },
        // the double arrows shift by the whole loaded window
        selected_day_count() {
          const days = this.quick_range_active_days
          return days > 0 ? days : 1
        },
        quick_range_active_days() {
          const from = moment(this.details_fromDate, 'MM-DD-YYYY')
          const to = moment(this.details_toDate, 'MM-DD-YYYY')
          return to.diff(from, 'days') + 1
        },
        // card order, not table order: id and the two times lead, then the
        // people and places, then the status message
        card_field_keys() {
          return ['reservation_id', 'scheduled_pickup_time', 'scheduled_end_time', 'driver_name', 'vehicle_name', 'from_location', 'to_location', 'status_msg']
        },
        // the role decides where a cell lands in the card grid, so the shared
        // stylesheet serves this page too even though it names columns its own way
        card_cell_roles() {
          return {
            reservation_id: 'title',
            scheduled_pickup_time: 'start',
            scheduled_end_time: 'finish',
            driver_name: 'driver',
            vehicle_name: 'vehicle',
            from_location: 'base',
            to_location: 'route',
          }
        },
        table_fields() {
          let fields = this.fields
          if (this.cardView) {
            const byKey = new Map(fields.map(field => [field.key, field]))
            fields = this.card_field_keys.map(key => byKey.get(key) || { key, label: '' })
          }
          return fields.map(field => ({
            ...field,
            tdClass: (value) => {
              if (!this.cardView)
                return ''
              const classes = ['actuals-card-cell--' + (this.card_cell_roles[field.key] || 'wide')]
              if (value === null || value === undefined || value === '')
                classes.push('actuals-card-cell--blank')
              return classes.join(' ')
            },
          }))
        },
        // b-table caches its column set, so switching modes needs a new key
        actuals_table_key() {
          return (this.cardView ? 'cards-' : '') + (this.selected_status || 'all')
        },
        actuals_sticky_header() {
          return this.cardView ? false : '70vh'
        },
        ...mapState({
          user: state => state.profile.user,
          viewAs: state => state.profile.view_as,
        }),
        isEffectiveAdmin() {
          return this.user.role === 'admin' && this.viewAs !== 'non-admin'
        },
        success_cnt() {
          return this.items_full.filter(item => item.status == 'success').length
        },
        error_cnt() {
          return this.items_full.filter(item => item.status == 'error').length
        },
        cancelled_cnt() {
          return this.items_full.filter(item => item.status == 'cancelled').length
        },
        skipped_cnt() {
          return this.items_full.filter(item => item.status == 'skipped').length
        },
        unprocessed_cnt() {
          return this.items_full.filter(item => item.status == 'unprocessed').length
        },
      },
      watch: {
        user: {
          deep: true,
          handler() {
            this.load_data()
          }
        },
        booking_filter() {
          this.filter_results()
        },
        selected_base() {
          this.filter_results()
        },
      }
    }
</script>

<style>

.error { background-color: rgba(255,0,0,0.25)}
.success { background-color: hsla(120,100%,75%,0.3)}
.unprocessed { background-color: hsla(200,40%,80%,0.3) }
.cancelled { background-color: hsla(290,60%,70%,0.3) }
.skipped { background-color: hsla(190,90%,80%,0.3) }

.error td { background-color: rgba(255,0,0,0.25)}
.success td {background-color: hsla(120,100%,75%,0.3)}
.unprocessed td { background-color: hsla(200,40%,80%,0.3) }
.cancelled td { background-color: hsla(290,60%,70%,0.3) }
.skipped td { background-color: hsla(190,90%,80%,0.3) }


div.row:nth-child(even) {
  background-color: #f2f2f2;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.header {
  font-weight: bold;
}

div.col {
  border: 1px solid grey;
}

.table td {
  padding: .1rem !important;
}

.mvmt-table td {
  vertical-align: middle !important;
}


/* toolbar / filter bar, same pass as actuals.vue (FI-2133) */
.actuals-page-root .actuals-fasttrak-toolbar {
  /* every control in the row is pinned to this. `height: auto` is not enough for
     <select>: it keeps a UA minimum content height that ignores the 12.5px
     line-height the chips and inputs shrink to, so the dropdowns would sit
     taller than everything beside them. */
  --actuals-toolbar-control-h: 28px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 6px 8px;
  font-size: 14px;
  font-weight: bold;
  margin: 14px 0 10px;
  position: relative;
}
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-sep {
  color: #adb5bd;
  font-size: 14px;
  user-select: none;
}
/* selects, inputs and buttons carry the date chips' type scale and box height so
   the toolbar reads as one row rather than two sizes of control side by side */
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-date,
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-select,
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-input,
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-btn,
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-label {
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.5;
}
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-date,
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-select,
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-input,
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-btn {
  height: var(--actuals-toolbar-control-h, 28px);
}
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-date,
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-input {
  padding: 0 10px;
}
/* the right-hand padding is the caret's gutter -- collapsing it to match the
   inputs would run the option text under the arrow. bootstrap sizes that gutter
   for 16px type, so it is pulled in with the caret to match the 12.5px text */
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-select {
  padding: 0 1.4rem 0 8px;
  background-position: right 0.45rem center;
  background-size: 8px 10px;
}
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border-radius: var(--fi-radius-pill, 999px);
  flex-shrink: 0;
}
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-date { width: 106px; }
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-select { width: 134px; }
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-type { width: 118px; }
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-base { width: 118px; }
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-input { width: 108px; }
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-label {
  white-space: nowrap;
  color: #6c757d;
}

@media (max-width: 768px) {
  /* the separator divided the dates from controls that are behind a button now */
  .actuals-page-root .actuals-fasttrak-toolbar .toolbar-sep {
    display: none;
  }
  .actuals-page-root .actuals-fasttrak-toolbar .actuals-date-presets {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
}

/* Status / overlap chips use the same compact sizing as the Trip and Feedback chips. */
.actuals-fasttrak-filter-chips .actuals-chip {
  font-size: 13px;
  font-weight: 600;
  padding: 2px 8px;
}
.actuals-fasttrak-filter-chips .actuals-chip-selected:not(.actuals-chip-total) {
  padding: 1px 7px;
}
.actuals-fasttrak-filter-chips .actuals-chip-total.actuals-chip-selected {
  padding: 2px 8px;
}
.actuals-fasttrak-filter-chips .filter-expander {
  font-size: 14px;
}
.actuals-resource-filter-select {
  width: 140px;
  font-size: 13px;
  font-weight: normal;
}
.actuals-page-root .actuals-fasttrak-toolbar .toolbar-group {
  display: inline-flex;
  align-items: center;
  gap: 4px 6px;
  flex-shrink: 0;
}

</style>

